/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow

import type { Element } from 'react';

import React, { Fragment } from 'react';
import moment from 'moment';
import trim from 'lodash/fp/trim.js';
import isEmpty from 'lodash/fp/isEmpty.js';

import { Input } from 'reactstrap';
import { Field } from 'redux-form';
import DatePicker from 'react-datepicker';
import { FieldFeedback } from '#components/support/forms/utility/index.js';

type RenderDatePicker = {
  id: string,
  disabled: boolean,
  meta: {
    touched: boolean,
    error: string,
  },
  inputClassName: string,
  input: {
    onChange: Function,
    value: string,
  },
};

type Props = {|
  form: string,
  name: string,
  validate?: Function[],
  inputClassName?: string,
|};

const renderDatePicker = (props: RenderDatePicker): any => {
  const { disabled, input, id, meta, inputClassName } = props;
  const { value, onChange, ...rest } = input;
  const { touched, error } = meta;
  const hasError = touched && !isEmpty(error);
  /* eslint-disable react/jsx-props-no-spreading */

  const selectedDate = value ? moment(value).toISOString() : null;

  const handleChange = date => {
    // When saving, treat the selected date as UTC
    if (!date) {
      onChange(null);
      return;
    }

    // Create UTC date from the local date picker value
    const dt = moment({
      year: date.getFullYear(),
      month: date.getMonth(),
      date: date.getDate(),
      hour: 0,
      minute: 0,
      second: 0,
    });

    onChange(dt.toISOString());
  };

  return (
    <DatePicker
      {...rest}
      id={id}
      selected={selectedDate}
      dateFormat="MM/dd/yy"
      onChange={handleChange}
      disabled={disabled}
      invalid={hasError}
      className={inputClassName}
      customInput={<Input />}
    />
  );
  /* eslint-enable react/jsx-props-no-spreading */
};

const DatePickerField = ({
  validate = [],
  name,
  form,
  inputClassName = '',
}: Props): Element<typeof Fragment> => (
  <>
    <div>
      <Field
        id={name}
        name={name}
        component={renderDatePicker}
        validate={validate}
        normalize={trim}
        inputClassName={inputClassName}
      />
    </div>
    <FieldFeedback name={name} form={form} />
  </>
);

export default DatePickerField;
